// @ts-nocheck
/**
 * The recipient's view of a shared Live Sheet: chrome-less, no app shell, no
 * sign-in wall. Everything about what they may see or do is decided by the
 * edge function against the token, on every request.
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import EventSheetTabs from "@/components/events/EventSheetTabs";
import { tabForScope } from "@/components/events/sheet-tabs/tabDefs";
import ShareSignInGate from "@/components/events/ShareSignInGate";

export default function SharedLiveSheetPage() {
  const { token } = useParams<{ token: string }>();
  const [eventId, setEventId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [denial, setDenial] = useState<string | null>(null);
  const [title, setTitle] = useState("Shared sheet");
  const [share, setShare] = useState<any>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const { data, error } = await supabase.functions.invoke("sheet-share", {
        body: { token },
      });
      if (cancelled) return;
      if (error) {
        setDenial(null);
        setMessage("This sheet couldn't be opened. Please contact the event organiser.");
        return;
      }
      if (data?.denied) {
        setDenial(data.denied);
        setMessage(data.message);
        return;
      }
      setDenial(null);
      setMessage(null);
      setShare(data?.share ?? null);
      setEventId(data?.share?.event_id ?? null);
      const name = data?.share?.event_name;
      if (name) {
        setTitle(name);
        document.title = `${name} — shared sheet`;
      }
    };


    load();

    // A magic-link return lands here with a fresh session; retry once it exists.
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") load();
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [token]);

  // The recipient has no account — give them the way in, not a dead end.
  if (denial === "sign_in" || denial === "not_allowed") {
    return <ShareSignInGate message={message ?? ""} eventName={null} />;
  }

  if (message) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="max-w-md rounded-md border bg-card p-6 text-center">
          <h1 className="text-base font-semibold">This sheet isn't available</h1>
          <p className="mt-2 text-sm text-muted-foreground">{message}</p>
        </div>
      </div>
    );
  }


  if (!eventId) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }

  const expiryLabel = share?.expires_at
    ? new Date(share.expires_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })
    : null;
  const bannerParts = [
    share?.organization_name ? `Shared by ${share.organization_name}` : `Shared sheet`,
    share?.permission === "edit" ? "can edit" : "view only",
    expiryLabel ? `expires ${expiryLabel}` : null,
  ].filter(Boolean);

  return (
    <div className="flex h-screen w-screen flex-col overflow-hidden bg-background">
      <div className="flex items-baseline gap-2 border-b bg-card px-3 py-2 text-xs text-muted-foreground">
        <span className="text-sm font-semibold text-foreground">{title}</span>
        <span>{bannerParts.join(" · ")}</span>
      </div>
      <div className="min-h-0 flex-1">
        <EventSheetTabs
          eventId={eventId}
          eventName={share?.event_name}
          shareToken={token}
          initialTab={share?.tab || tabForScope(share?.scope)}
          popout
        />
      </div>
    </div>
  );

}
